import { type FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ReactDataSheet from 'react-datasheet';
import 'react-datasheet/lib/react-datasheet.css';

import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type Chart as ChartElement } from '@editor2/types/pageElements';
import { type GridElement } from '@editor2/types/gridElement';

import ColorPicker from './ColorPicker';
import RowOptionsCell from './RowOptionsCell';
import ColumnHeader from '@editor2/components/DataTable/ColumnOptionsCell';

import { mapDataStyle } from '../GeoMap/styles';
import { chartDataTableStyle, chartOptionsBlockStyles } from './styles';
import { Button } from 'react-bootstrap';
import { BsPlus } from 'react-icons/bs';

class MyReactDataSheet extends ReactDataSheet<GridElement, any> {}

interface Props {
	element: ChartElement;
	currentPageData: any[];
	courseId: number;
	currentPage: number;
	limit: number;
}

const DEFAULT_COLOR = '#123456';
const DEFAULT_COLUMN_HEADER = 'Column';

const ChartDataTable: FC<Props> = (props) => {
	const { element, courseId, currentPageData, currentPage, limit } = props;

	const [grid, setGrid] = useState([]);
	const dispatch = useDispatch();

	const commonPayload = { elementId: element.family_id, courseId };

	const onContextMenu = (e, cell, i, j) => (cell.readOnly ? e.preventDefault() : null);

	const onCellsChanged = (changes) => {
		let updatedData = [...element.data];

		changes.forEach(({ row, col, value }) => {
			if (row === 0) {
				handleColorChange({ index: col - 1, value });
			} else if (row === 1) {
				// headers row
				const tempRow = updatedData[0];
				const updatedRow = [...tempRow.slice(0, col), value, ...tempRow.slice(col + 1)];

				updatedData = [updatedRow, ...updatedData.slice(1)];
			} else {
				const rowIndex = limit * (currentPage - 1) + row - 1;
				const tempRow = updatedData[rowIndex];
				const updatedRow = [...tempRow.slice(0, col), value, ...tempRow.slice(col + 1)];

				updatedData = [
					...updatedData.slice(0, rowIndex),
					updatedRow,
					...updatedData.slice(rowIndex + 1)
				];
			}
		});

		dispatch(updateElement({ ...commonPayload, data: updatedData }));
	};

	const onAddColumnClick = (index: number) => {
		const updatedData = element.data.map((row, ind) => {
			if (ind === 0) return [...row.slice(0, index), DEFAULT_COLUMN_HEADER, ...row.slice(index)];
			return [...row.slice(0, index), '', ...row.slice(index)];
		});

		const colorIndex = index === 0 ? 0 : index - 1;
		const updatedColors = [
			...element.colors.slice(0, colorIndex),
			DEFAULT_COLOR,
			...element.colors.slice(colorIndex)
		];

		dispatch(updateElement({ ...commonPayload, data: updatedData, colors: updatedColors }));
	};

	const onDeleteColumnClick = (index: number) => {
		const updatedData = element.data.map((row) => {
			return [...row.slice(0, index), ...row.slice(index + 1)];
		});

		const colorIndex = index === 0 ? 0 : index - 1;
		const updatedColors = [
			...element.colors.slice(0, colorIndex),
			...element.colors.slice(colorIndex + 1)
		];

		dispatch(updateElement({ ...commonPayload, data: updatedData, colors: updatedColors }));
	};

	const handleColorChange = ({ index, value }) => {
		let updatedColors = [...element.colors];

		updatedColors = [...updatedColors.slice(0, index), value, ...updatedColors.slice(index + 1)];

		dispatch(updateElement({ ...commonPayload, colors: updatedColors }));
	};

	const generateColorsRow = () => {
		const colors = element.colors.map((color, index) => ({
			value: <ColorPicker defaultColor={color} index={index} onChange={handleColorChange} />,
			expr: color
		}));
		return [{ value: '', readOnly: true }, ...colors, { readOnly: true }];
	};

	const generateHeaderRow = () => {
		const headers = element.data[0].map((header, index) => ({
			value: (
				<ColumnHeader
					label={header as string}
					index={index}
					onAddColumnClick={onAddColumnClick}
					onDeleteColumnClick={onDeleteColumnClick}
				/>
			),
			expr: header
		}));

		return [...headers, { readOnly: true }];
	};

	const onAddRowClick = (index) => {
		const newRow = new Array(element.data[0].length).fill('');
		const rowIndex = limit * (currentPage - 1) + index;

		const updatedData = [
			...element.data.slice(0, rowIndex),
			newRow,
			...element.data.slice(rowIndex)
		];

		dispatch(updateElement({ ...commonPayload, data: updatedData }));
	};

	const onDeleteRowClick = (index) => {
		const rowIndex = limit * (currentPage - 1) + index;

		const updatedData = [...element.data.slice(0, rowIndex), ...element.data.slice(rowIndex + 1)];

		dispatch(updateElement({ ...commonPayload, data: updatedData }));
	};

	const generateRow = (rowData, index) => {
		const row = rowData.map((item) => ({ value: item }));
		const optionsCell = {
			value: (
				<RowOptionsCell
					index={index + 1}
					onAddRowClick={onAddRowClick}
					onDeleteRowClick={onDeleteRowClick}
				/>
			),
			readOnly: true
		};

		return [...row, optionsCell];
	};

	const generateGrid = () => {
		const colorsRow = generateColorsRow();
		const headersRow = generateHeaderRow();

		const rows = currentPageData.map((row, index) => generateRow(row, index));

		return [colorsRow, headersRow, ...rows];
	};

	useEffect(() => {
		const rows = generateGrid();
		setGrid(rows);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPageData, limit, element.data, element.colors]);

	return (
		<>
			<div className={chartOptionsBlockStyles}>
				<Button
					variant="outline-secondary"
					size="sm"
					onClick={() => onAddRowClick(element.data.length)}>
					<BsPlus />
					Add row
				</Button>

				<Button
					variant="outline-secondary"
					size="sm"
					onClick={() => onAddColumnClick(element.data[0].length)}>
					<BsPlus />
					Add column
				</Button>
			</div>

			<MyReactDataSheet
				className={`${mapDataStyle} ${chartDataTableStyle}`}
				data={grid}
				valueRenderer={(cell) => cell.value}
				dataRenderer={(cell) => cell.expr || cell.value}
				onContextMenu={onContextMenu}
				onCellsChanged={onCellsChanged}
			/>
		</>
	);
};

export default ChartDataTable;
